import { Source, Layer } from 'react-map-gl/mapbox';
import type { LineLayerSpecification } from 'mapbox-gl';
import { useGcsStore } from '../../../store/useGcsStore';
import { selectVehicleStatusCategory } from '../../../store/selectors/selectVehicleStatusCategory';
import { type MissionId } from '../../../types/gcs/mission';
import {
  VehicleStatusCategory,
  type VehicleStatusCategory as VehicleStatusCategoryType
} from '../../../types/ui/vehicleStatusCategory';
import { isSegmentValid } from './lockedSegmentAnchor';

interface SingleMissionRouteLockedSegmentsPolylineProps {
  gcsId: string;
  missionId: MissionId;
}

const getLockedSegmentColor = (vehicleCategory: VehicleStatusCategoryType | null): string => {
  // Mapbox GL paint properties for polylines do not resolve CSS custom properties (var(--...)).
  // Keep this as a concrete color string returned by getLockedSegmentColor.
  switch (vehicleCategory) {
    case VehicleStatusCategory.INFLIGHT_FAILURE_MODE:
      return '#ec221f';
    case VehicleStatusCategory.INFLIGHT_NOMINAL:
      return '#0090FF';
    case VehicleStatusCategory.READY_FOR_TAKEOFF:
      return '#14ae5c';
    case VehicleStatusCategory.WAITING_ON_DOCK:
      return '#D6761C';
    case VehicleStatusCategory.POSTFLIGHT:
    case VehicleStatusCategory.ON_GROUND:
      return '#ffd249';
    default:
      return '#000000';
  }
};

const createLockedSegmentLineLayer = (vehicleCategory: VehicleStatusCategoryType | null): LineLayerSpecification => ({
  id: 'locked-segment-line',
  type: 'line',
  source: 'locked-segment',
  paint: {
    'line-color': getLockedSegmentColor(vehicleCategory),
    'line-width': 4,
    'line-opacity': 1
  }
});

export const SingleMissionRouteLockedSegmentsPolyline = ({
  gcsId,
  missionId
}: SingleMissionRouteLockedSegmentsPolylineProps) => {
  const routeData = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.missionRouteMap?.get(missionId);
  });

  const lockingInfo = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.missionLockingInfoMap?.get(missionId);
  });

  const vehicleCategory = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;

    const liveMission = Array.from(gcsData.liveMissionsMap.values()).find((mission) => mission.missionId === missionId);
    if (!liveMission) return null;

    return selectVehicleStatusCategory(state.data, gcsId, liveMission.vehicleId);
  });

  if (!routeData?.route || routeData.route.length === 0) {
    return null;
  }

  if (!lockingInfo?.lockedSegmentsIndices || lockingInfo.lockedSegmentsIndices.length === 0) {
    return null;
  }

  return (
    <>
      {lockingInfo.lockedSegmentsIndices.map((segment, segmentIndex) => {
        // Validate segment before drawing
        if (!isSegmentValid(routeData.route.length, segment)) {
          return null;
        }

        // Extract waypoints for this locked segment
        const segmentWaypoints = routeData.route.slice(segment.startIndex, segment.endIndex + 1);

        const coordinates = segmentWaypoints.map((waypoint) => [waypoint.location.lng, waypoint.location.lat]);

        const segmentGeoJSON = {
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates
          },
          properties: {
            missionId: lockingInfo.missionId,
            segmentIndex,
            startIndex: segment.startIndex,
            endIndex: segment.endIndex
          }
        };

        const lockedSegmentLineLayer = createLockedSegmentLineLayer(vehicleCategory);
        const sourceId = `locked-segment-${gcsId}-${missionId}-${segmentIndex}`;
        const layerId = `locked-segment-line-${gcsId}-${missionId}-${segmentIndex}`;

        return (
          <Source key={sourceId} id={sourceId} type='geojson' data={segmentGeoJSON}>
            <Layer {...lockedSegmentLineLayer} id={layerId} source={sourceId} />
          </Source>
        );
      })}
    </>
  );
};
